import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useStorefront } from './useStorefront';

/**
 * SSOT for the VAT rate applied on the ACTIVE storefront.
 *
 * Rate is read exclusively from `public.vat_settings` keyed by the
 * storefront's country code (CH→CH, AT→AT, …). No hardcoded rates in
 * code, no silent 8.1 % fallback: an admin-editable single source.
 *
 * States:
 *   loading=true                   → still fetching, callers must WAIT.
 *   loading=false, rate=number     → use `rate` for display + charge.
 *   loading=false, error=Error     → missing/inactive row for a live
 *                                    storefront: BLOCK checkout, surface
 *                                    the error. Never fall back silently.
 */
export interface StorefrontVatState {
  rate: number | null;
  loading: boolean;
  error: Error | null;
}

// Module-level cache keyed by country code so SideCart + CheckoutPage
// share one fetch per session.
const cache = new Map<string, number>();

export function useStorefrontVat(): StorefrontVatState {
  const { countryCode } = useStorefront();
  const cached = cache.get(countryCode);
  const [rate, setRate] = useState<number | null>(cached ?? null);
  const [loading, setLoading] = useState<boolean>(cached === undefined);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (cache.has(countryCode)) {
      setRate(cache.get(countryCode)!);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: qErr } = await supabase
        .from('vat_settings')
        .select('vat_rate')
        .eq('country_code', countryCode)
        .eq('is_active', true)
        .maybeSingle();
      if (cancelled) return;
      if (qErr) {
        setError(qErr);
        setLoading(false);
        return;
      }
      if (!data) {
        setError(new Error(
          `Missing active vat_settings row for storefront country '${countryCode}'.`
        ));
        setLoading(false);
        return;
      }
      const value = Number(data.vat_rate);
      cache.set(countryCode, value);
      setRate(value);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [countryCode]);

  return { rate, loading, error };
}